import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  Alert,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import {
  CheckCircle as ActivateIcon,
  Cancel as SuspendIcon,
  Email as EmailIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  ArrowDropUp as ArrowDropUpIcon,
  SwapVert as StatusIcon,
} from "@mui/icons-material";
import type { UserListItem } from "@document-rag/shared";
import {
  useBulkOperations,
  BulkOperationResult,
} from "../../features/users/hooks/useBulkOperations";

interface BulkActionsBarProps {
  selectedUsers: UserListItem[];
  onClearSelection: () => void;
  onOperationComplete: () => void;
}

type BulkAction =
  | "activate"
  | "suspend"
  | "email"
  | "archive"
  | "delete"
  | "lock";

interface ActionConfig {
  title: string;
  message: string;
  confirmButtonText: string;
  confirmButtonColor: "primary" | "error";
  icon: React.ReactNode;
}

const BulkActionsBar: React.FC<BulkActionsBarProps> = ({
  selectedUsers,
  onClearSelection,
  onOperationComplete,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState<BulkAction | null>(null);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [operationResult, setOperationResult] =
    useState<BulkOperationResult<string> | null>(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(
    null,
  );

  const {
    bulkUpdateUserStatus,
    bulkSendOnboardingEmails,
    bulkArchiveUsers,
    bulkDeleteUsers,
    progress,
    resetProgress,
  } = useBulkOperations();

  // Get count of users that can receive onboarding emails (CREATED status)
  const onboardingEligibleCount = selectedUsers.filter(
    (user) =>
      user.accountStatus === "CREATED" || user.accountStatus === "ONBOARDING",
  ).length;

  // Action configurations
  const actionConfigs: Record<BulkAction, ActionConfig> = {
    activate: {
      title: "Activate Users",
      message: `Are you sure you want to activate ${selectedUsers.length} selected user${selectedUsers.length > 1 ? "s" : ""}?`,
      confirmButtonText: "Activate",
      confirmButtonColor: "primary",
      icon: <ActivateIcon />,
    },
    suspend: {
      title: "Suspend Users",
      message: `Are you sure you want to suspend ${selectedUsers.length} selected user${selectedUsers.length > 1 ? "s" : ""}?`,
      confirmButtonText: "Suspend",
      confirmButtonColor: "primary",
      icon: <SuspendIcon />,
    },
    email: {
      title: "Send Onboarding Emails",
      message:
        onboardingEligibleCount > 0
          ? `Send onboarding emails to ${onboardingEligibleCount} user${onboardingEligibleCount > 1 ? "s" : ""}? This will send emails to users with CREATED status and resend to users with ONBOARDING status.`
          : "No eligible users selected. Only users with CREATED or ONBOARDING status can receive onboarding emails.",
      confirmButtonText: "Send Emails",
      confirmButtonColor: "primary",
      icon: <EmailIcon />,
    },
    archive: {
      title: "Archive Users",
      message: `Are you sure you want to archive ${selectedUsers.length} selected user${selectedUsers.length > 1 ? "s" : ""}?`,
      confirmButtonText: "Archive",
      confirmButtonColor: "primary",
      icon: <ArchiveIcon />,
    },
    delete: {
      title: "Delete Users",
      message: `Are you sure you want to delete ${selectedUsers.length} selected user${selectedUsers.length > 1 ? "s" : ""}? This action cannot be undone.`,
      confirmButtonText: "Delete",
      confirmButtonColor: "error",
      icon: <DeleteIcon />,
    },
    lock: {
      title: "Lock Users",
      message: `Are you sure you want to lock ${selectedUsers.length} selected user${selectedUsers.length > 1 ? "s" : ""}? Locked users cannot log in.`,
      confirmButtonText: "Lock",
      confirmButtonColor: "primary",
      icon: <LockIcon />,
    },
  };

  // Handle action click
  const handleActionClick = (action: BulkAction) => {
    // Close status menu if open
    setStatusMenuAnchor(null);
    setCurrentAction(action);
    setDialogOpen(true);
  };

  // Handle status menu
  const handleStatusMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setStatusMenuAnchor(event.currentTarget);
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchor(null);
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setDialogOpen(false);
    setCurrentAction(null);
  };

  // Handle confirm action
  const handleConfirmAction = async () => {
    if (!currentAction) return;

    handleDialogClose();

    const userIds = selectedUsers.map((user) => user.id);
    let result: BulkOperationResult<string>;

    try {
      switch (currentAction) {
        case "activate":
          result = await bulkUpdateUserStatus(userIds, "ACTIVE");
          break;
        case "suspend":
          result = await bulkUpdateUserStatus(userIds, "SUSPENDED");
          break;
        case "email": {
          // Only send to users in CREATED or ONBOARDING status
          const eligibleUserIds = selectedUsers
            .filter(
              (user) =>
                user.accountStatus === "CREATED" ||
                user.accountStatus === "ONBOARDING",
            )
            .map((user) => user.id);
          result = await bulkSendOnboardingEmails(eligibleUserIds);
          break;
        }
        case "archive":
          result = await bulkArchiveUsers(userIds);
          break;
        case "delete":
          result = await bulkDeleteUsers(userIds);
          break;
        case "lock":
          result = await bulkUpdateUserStatus(userIds, "LOCKED");
          break;
        default:
          return;
      }

      setOperationResult(result);
      setResultDialogOpen(true);
    } catch (error) {
      console.error("Bulk operation failed:", error);
    }
  };

  // Handle result dialog close
  const handleResultDialogClose = () => {
    setResultDialogOpen(false);
    setOperationResult(null);
    resetProgress();
    onClearSelection();
    onOperationComplete();
  };

  // Get sample of selected users for preview
  const userPreview = selectedUsers.slice(0, 5);
  const remainingCount = selectedUsers.length - userPreview.length;

  return (
    <>
      <AppBar
        position="fixed"
        color="default"
        elevation={8}
        sx={{
          top: "auto",
          bottom: 0,
          borderTop: 2,
          borderColor: "primary.main",
        }}
      >
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 0, mr: 3 }}>
            {selectedUsers.length} user{selectedUsers.length > 1 ? "s" : ""}{" "}
            selected
          </Typography>

          <Box sx={{ flexGrow: 1, display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<StatusIcon />}
              endIcon={<ArrowDropUpIcon />}
              onClick={handleStatusMenuOpen}
            >
              Change Status
            </Button>

            <Tooltip
              title={
                onboardingEligibleCount === 0
                  ? "No users with CREATED or ONBOARDING status selected"
                  : `Send onboarding emails to ${onboardingEligibleCount} user${onboardingEligibleCount === 1 ? "" : "s"}`
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="info"
                  startIcon={<EmailIcon />}
                  onClick={() => handleActionClick("email")}
                  disabled={onboardingEligibleCount === 0}
                >
                  Send Onboarding
                  {onboardingEligibleCount > 0 &&
                    ` (${onboardingEligibleCount})`}
                </Button>
              </span>
            </Tooltip>

            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => handleActionClick("delete")}
            >
              Delete
            </Button>
          </Box>

          <Button variant="text" onClick={onClearSelection}>
            Clear Selection
          </Button>
        </Toolbar>
      </AppBar>

      {/* Confirmation Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        {currentAction && (
          <>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {actionConfigs[currentAction].icon}
              {actionConfigs[currentAction].title}
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                {actionConfigs[currentAction].message}
              </DialogContentText>

              {userPreview.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected users:
                  </Typography>
                  <List dense>
                    {userPreview.map((user) => (
                      <ListItem key={user.id}>
                        <ListItemText
                          primary={`${user.firstName} ${user.lastName}`}
                          secondary={user.email}
                        />
                        <Chip
                          label={user.accountStatus}
                          size="small"
                          color={
                            user.accountStatus === "ACTIVE"
                              ? "success"
                              : "default"
                          }
                        />
                      </ListItem>
                    ))}
                    {remainingCount > 0 && (
                      <ListItem>
                        <ListItemText
                          primary={`...and ${remainingCount} more`}
                        />
                      </ListItem>
                    )}
                  </List>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose}>Cancel</Button>
              <Button
                onClick={handleConfirmAction}
                variant="contained"
                color={actionConfigs[currentAction].confirmButtonColor}
              >
                {actionConfigs[currentAction].confirmButtonText}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Progress Dialog */}
      <Dialog open={progress.status === "processing"} maxWidth="sm" fullWidth>
        <DialogTitle>Processing...</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {progress.message}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(progress.current / progress.total) * 100}
              sx={{ mt: 1 }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Results Dialog */}
      <Dialog
        open={resultDialogOpen}
        onClose={handleResultDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Operation Complete</DialogTitle>
        <DialogContent>
          {operationResult && (
            <>
              <Alert
                severity={
                  operationResult.failed.length === 0 ? "success" : "warning"
                }
                sx={{ mb: 2 }}
              >
                {operationResult.successful.length} successful,{" "}
                {operationResult.failed.length} failed
              </Alert>

              {operationResult.failed.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Failed operations:
                  </Typography>
                  <List dense>
                    {operationResult.failed.map((failure, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={failure.item}
                          secondary={failure.error}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResultDialogClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Change Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={handleStatusMenuClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <MenuItem onClick={() => handleActionClick("activate")}>
          <ActivateIcon sx={{ mr: 1 }} fontSize="small" />
          Activate
        </MenuItem>
        <MenuItem onClick={() => handleActionClick("suspend")}>
          <SuspendIcon sx={{ mr: 1 }} fontSize="small" />
          Suspend
        </MenuItem>
        <MenuItem onClick={() => handleActionClick("lock")}>
          <LockIcon sx={{ mr: 1 }} fontSize="small" />
          Lock
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleActionClick("archive")}>
          <ArchiveIcon sx={{ mr: 1 }} fontSize="small" />
          Archive
        </MenuItem>
      </Menu>
    </>
  );
};

export default BulkActionsBar;
